import { Icon } from '#/components/Icon/Icon';
import { IdealistLogo } from '#/components/Logo/IdealistLogo';
import { getI18n } from '#/i18n/getI18n';
import { I18N_LOCALES, I18nLocale } from '#/i18n/types/I18nLocale';
import { homeRoute } from '#/routing/routes';
import { LOCALE_NAMES } from '#/utils/constants/general/locales';

import { PageFooterCentralLinks } from './CentralLinks/PageFooterCentralLinks';
import { pageFooterDataByLocale } from './data/pageFooterDataByLocale';
import {
  PageFooterBottom,
  PageFooterCopyright,
  PageFooterHolder,
  PageFooterLogoLink,
  PageFooterLogoMissionHolder,
  PageFooterMissionStatement,
  PageFooterSectionHolder,
  PageFooterSectionLink,
  PageFooterSectionLinksHolder,
  PageFooterSectionsAndLogoMissionHolder,
  PageFooterSectionsHolder,
  PageFooterSectionTitle,
  PageFooterSocialLink,
  PageFooterSocialLinksHolder,
  PageFooterLocaleLinksHolder,
  PageFooterLocaleLink,
} from './PageFooter.styled';
import {
  pageFooterDataByLinkType,
  pageFooterSocialLinks,
} from './pageFooterConstants';
import { pageFooterOrderedSectionsMetadata } from './pageFooterOrderedSectionsMetadata';
import { PageFooterVolunteeringLinks } from './VolunteeringLinks/PageFooterVolunteeringLinks';

type Props = {
  locale: I18nLocale;
  urlByLocale: Record<I18nLocale, string | undefined> | undefined;
  showDivider: boolean;
};

export function PageFooter({ locale, urlByLocale, showDivider }: Props) {
  const { i18n } = getI18n(locale);

  const data = pageFooterDataByLocale[locale];

  return (
    <PageFooterHolder data-qa-id="footer" $showDivider={showDivider}>
      <PageFooterSectionsAndLogoMissionHolder $showDivider={showDivider}>
        <PageFooterSectionsHolder>
          {pageFooterOrderedSectionsMetadata
            .map(({ sectionName }) => {
              const section = data[sectionName];

              if (!section) return null;

              return (
                <PageFooterSectionHolder
                  key={sectionName}
                  $isEsOrPt={
                    locale === I18nLocale.ES || locale === I18nLocale.PT
                  }
                >
                  <PageFooterSectionTitle>
                    {section.title}
                  </PageFooterSectionTitle>

                  <PageFooterSectionLinksHolder>
                    {section.links.map((link) => {
                      if (!pageFooterDataByLinkType[link.type]) {
                        return null;
                      }
                      const { trackClickedName } =
                        pageFooterDataByLinkType[link.type];

                      if (link.href.startsWith('https://')) {
                        return (
                          <PageFooterSectionLink
                            key={link.title}
                            type="link"
                            href={link.href}
                            trackClicked={{
                              name: trackClickedName,
                              properties: { context: 'Footer' },
                            }}
                            target="_blank"
                          >
                            {link.title}
                          </PageFooterSectionLink>
                        );
                      } else {
                        return (
                          <PageFooterSectionLink
                            key={link.title}
                            type="link"
                            href={link.href}
                            trackClicked={{
                              name: trackClickedName,
                              properties: { context: 'Footer' },
                            }}
                          >
                            {link.title}
                          </PageFooterSectionLink>
                        );
                      }
                    })}
                  </PageFooterSectionLinksHolder>
                </PageFooterSectionHolder>
              );
            })
            .filter(Boolean)}
        </PageFooterSectionsHolder>

        <PageFooterLogoMissionHolder $showDivider={showDivider}>
          <PageFooterLogoLink
            type="link"
            href={homeRoute.with({ lang: locale })}
            trackClicked={{ name: 'Footer Logo' }}
          >
            <IdealistLogo withText height={56} />
          </PageFooterLogoLink>

          <PageFooterMissionStatement>
            {data.missionStatement}
          </PageFooterMissionStatement>

          <PageFooterSocialLinksHolder>
            {pageFooterSocialLinks(i18n)[locale].map(
              ({ href, eventName, iconName }) => (
                <PageFooterSocialLink
                  type="link"
                  href={href}
                  key={iconName}
                  trackClicked={{ name: eventName }}
                  aria-label={eventName}
                >
                  <Icon name={iconName} size={20} colorName="white" />
                </PageFooterSocialLink>
              ),
            )}
          </PageFooterSocialLinksHolder>
        </PageFooterLogoMissionHolder>
      </PageFooterSectionsAndLogoMissionHolder>

      <PageFooterBottom>
        <PageFooterLocaleLinksHolder>
          {I18N_LOCALES.map((linkLocale) => {
            const localeUrl = urlByLocale?.[linkLocale];
            const enabled = linkLocale !== locale && localeUrl;

            return (
              <PageFooterLocaleLink
                key={linkLocale}
                data={
                  enabled
                    ? { type: 'link', anchorType: 'native', href: localeUrl }
                    : { type: 'inert' }
                }
                data-locale-link={enabled ? linkLocale : undefined}
                qaId={
                  enabled
                    ? `locale-link-${linkLocale}`
                    : `disabled-locale-link-${linkLocale}`
                }
                title={
                  localeUrl
                    ? undefined
                    : i18n(
                        'This page is currently not available in %(language)s',
                        {
                          language: LOCALE_NAMES[linkLocale],
                        },
                      )
                }
              >
                {LOCALE_NAMES[linkLocale]}
              </PageFooterLocaleLink>
            );
          })}
        </PageFooterLocaleLinksHolder>

        {locale === I18nLocale.EN && <PageFooterCentralLinks />}

        <PageFooterCopyright>
          {i18n('Copyright © %(year)s Idealist.org', {
            year: new Date().getFullYear(),
          })}
        </PageFooterCopyright>
      </PageFooterBottom>

      {locale === I18nLocale.EN && <PageFooterVolunteeringLinks />}
    </PageFooterHolder>
  );
}
